import { Card, CardHeader, CardTitle, CardContent, Button, Alert } from '../components'

interface VerificationStatusPageProps {
  token: string
}

type Tone = 'success' | 'error' | 'info'

// Didit's own session-status vocabulary (see backend/src/didit-client.ts's
// DiditStatus/mapDiditStatus) — mirrored here purely for a friendly label,
// not trusted as the real outcome.
const STATUS_COPY: Record<string, { label: string; tone: Tone }> = {
  Approved: { label: 'Approved', tone: 'success' },
  Declined: { label: 'Declined', tone: 'error' },
  Abandoned: { label: 'Not completed', tone: 'error' },
  Expired: { label: 'Expired', tone: 'error' },
  'Kyc Expired': { label: 'Expired', tone: 'error' },
  'In Review': { label: 'In review', tone: 'info' },
  'In Progress': { label: 'In progress', tone: 'info' },
  'Not Started': { label: 'Not started', tone: 'info' },
}

// Dedicated landing page for Didit's hosted-verification redirect (see
// backend/src/index.ts's startDiditVerification callback). This is an
// unsigned browser hand-off, not the authoritative result — that's the
// separate signed webhook, which has already updated the identity check row
// server-side by the time this page can even render. So this page never
// calls the backend: it just gives the person immediate, friendly feedback
// from the redirect's own query params, then points them at their portal
// (/portal/{token}) where the real, confirmed status lives.
export const VerificationStatusPage: React.FC<VerificationStatusPageProps> = ({ token }) => {
  const params = new URLSearchParams(window.location.search)
  const rawStatus = params.get('status')
  const copy = rawStatus ? STATUS_COPY[rawStatus] : undefined

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Card>
          <CardHeader>
            <CardTitle>Verification {copy?.label ?? 'Submitted'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert type={copy?.tone ?? 'info'}>
              {rawStatus
                ? `Your ID verification was reported as "${copy?.label ?? rawStatus}". This can take a few minutes to confirm.`
                : 'Your ID verification session has ended. This can take a few minutes to confirm.'}
            </Alert>
            <p className="text-sm text-gray-600">
              Head back to your portal to see the confirmed status alongside your other tasks and documents.
            </p>
            <a href={`/portal/${token}`}>
              <Button type="button" className="w-full">View My Portal</Button>
            </a>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
